// src/components/pipsCreatureKeeper/KeeperNamePrompt.tsx
import { logger } from '../../../utils/logger';
// FIXED: Button now properly submits and handles loading state, emojis fixed, and state resets on modal close

import React, { useState, useEffect } from 'react';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonInput,
  IonItem,
  IonLabel,
  IonCard,
  IonCardContent,
  IonText,
  IonSpinner
} from '@ionic/react';

interface KeeperNamePromptProps {
  isOpen: boolean;
  onSubmit: (name: string) => Promise<void>;
}

const KeeperNamePrompt: React.FC<KeeperNamePromptProps> = ({ isOpen, onSubmit }) => {
  const [keeperName, setKeeperName] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state when modal closes to prevent infinite spinner
  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false);
      setError('');
      // Don't reset keeperName to preserve input if modal reopens
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    const trimmedName = keeperName.trim();

    if (!trimmedName) {
      setError('Please enter a name for your Keeper');
      return;
    }

    if (trimmedName.length < 2) {
      setError('Keeper name must be at least 2 characters');
      return;
    }

    if (trimmedName.length > 30) {
      setError('Keeper name must be less than 30 characters');
      return;
    }

    setError('');
    setIsSubmitting(true);
    logger.log('🏗️ KeeperNamePrompt: Submitting keeper name:', trimmedName);

    try {
      await onSubmit(trimmedName);
      logger.log('✅ KeeperNamePrompt: onSubmit completed successfully');
      // Don't reset isSubmitting here - let the parent close the modal
    } catch (err: any) {
      logger.error('❌ KeeperNamePrompt: Error submitting keeper name:', err);
      logger.error('Error details:', {
        message: err?.message,
        stack: err?.stack,
        fullError: err
      });
      setError(err?.message || 'Failed to create game. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSubmitting && keeperName.trim()) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      backdropDismiss={false}
      className="keeper-name-modal"
    >
      <IonHeader>
        <IonToolbar color="success">
          <IonTitle>🌿 Welcome, Habitat Specialist</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <div style={{
          maxWidth: '500px',
          margin: '40px auto 0',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <IonCard>
            <IonCardContent>
              <IonText>
                <p style={{
                  fontSize: '1.1rem',
                  lineHeight: '1.6',
                  margin: '0 0 20px 0',
                  textAlign: 'center'
                }}>
                  Before you begin your journey to restore the sanctuary,
                  what shall we call you, Keeper?
                </p>
              </IonText>

              <IonItem>
                <IonLabel position="stacked">Your Keeper Name</IonLabel>
                <IonInput
                  value={keeperName}
                  onIonInput={(e) => {
                    setKeeperName(e.detail.value || '');
                    setError('');
                  }}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter your name..."
                  maxlength={30}
                  autocomplete="off"
                  autoFocus
                  disabled={isSubmitting}
                />
              </IonItem>

              {error && (
                <IonText color="danger">
                  <p style={{
                    fontSize: '0.9rem',
                    margin: '8px 0 0 0',
                    textAlign: 'center'
                  }}>
                    {error}
                  </p>
                </IonText>
              )}

              <IonButton
                expand="block"
                color="success"
                onClick={handleSubmit}
                disabled={!keeperName.trim() || isSubmitting}
                style={{ marginTop: '20px' }}
              >
                {isSubmitting ? (
                  <>
                    <IonSpinner name="crescent" style={{ marginRight: '8px' }} />
                    Creating Sanctuary...
                  </>
                ) : (
                  'Begin Your Journey'
                )}
              </IonButton>
            </IonCardContent>
          </IonCard>

          <IonText color="medium">
            <p style={{
              fontSize: '0.85rem',
              textAlign: 'center',
              margin: 0,
              fontStyle: 'italic'
            }}>
              This name will be used to identify your sanctuary
            </p>
          </IonText>
        </div>
      </IonContent>
    </IonModal>
  );
};

export default KeeperNamePrompt;